// Shared host-platform + project-scope helpers used by the gated report,
// the full report migration card, and the quote page.

// Platforms that are inherently heavier to migrate/modernize (plugins, stores, custom themes).
export const HEAVY_PLATFORMS = new Set([
  "Shopify", "WordPress", "Drupal", "Joomla", "Magento",
]);

const SCOPE_ORDER = ["small", "medium", "large"];

export const SCOPE_META = {
  small: { key: "small", emoji: "🟢", label: "Small project", color: "#32D74B" },
  medium: { key: "medium", emoji: "🟡", label: "Medium project", color: "#FFD60A" },
  large: { key: "large", emoji: "🔴", label: "Large project", color: "#FF3B30" },
};

// Estimate the project size from scan signals, then nudge it up one level for
// heavier host platforms (e.g. Shopify stores, plugin-heavy WordPress).
export function computeScope({ data = {}, comp = {}, seo = {}, secGaps = [] }) {
  const s = data.scores || {};
  const vals = [s.frontend, s.mobile, s.security, s.seo, s.compliance, s.integration]
    .filter((v) => typeof v === "number");
  const overall = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;

  const allIssues = [...(comp.flags || []), ...(seo.flags || []), ...secGaps];
  const total = allIssues.length;
  const crit = allIssues.filter((f) => f.severity === "critical").length;
  const high = allIssues.filter((f) => f.severity === "high").length;

  let key;
  if (!data.reachable || crit > 0 || (overall != null && overall < 45) || total >= 12) key = "large";
  else if (high > 0 || (overall != null && overall < 70) || total >= 5) key = "medium";
  else key = "small";

  const hosting = data.hosting || {};
  const heavy = (hosting.platform && HEAVY_PLATFORMS.has(hosting.platform)) || (hosting.dependencyCount || 0) >= 6;
  const baseKey = key;
  let nudged = false;
  if (heavy) {
    const idx = SCOPE_ORDER.indexOf(key);
    if (idx >= 0 && idx < SCOPE_ORDER.length - 1) {
      key = SCOPE_ORDER[idx + 1];
      nudged = true;
    }
  }

  return {
    ...SCOPE_META[key], total, crit, high, overall, heavy, nudged,
    platform: hosting.platform || null,
    baseKey, baseLabel: SCOPE_META[baseKey].label,
  };
}

// Scanner dependency category -> savings-calculator (VENDOR_CATALOG) key.
const CAT_TO_CALCKEY = {
  "Chat / support widget": "live_chat",
  "Booking / scheduling": "booking",
};

// Build a /migration querystring that pre-fills the savings calculator from a scan.
export function calculatorQuery(hosting = {}) {
  const params = new URLSearchParams();
  if (hosting.platform) params.set("website_host", hosting.platform);
  for (const d of hosting.dependencies || []) {
    const key = CAT_TO_CALCKEY[d.category];
    if (key && !params.has(key)) params.set(key, d.name);
  }
  const qs = params.toString();
  return qs ? `/migration?${qs}` : "/migration";
}
